import { audio } from '../../game/audio/Audio';
import { social } from '../api/social';
import { setWallet } from './store';
import { loadInbox, refreshMe } from './actions';
import { launch, onMatchFound, onQueueUpdate, onRewards } from './play';
import { acceptFriend, appendChat, declineFriend, joinParty, loadFriends, loadMyClan, setChatHistory } from './socialActions';
import { friends, me, mutedUsers, onlineCount, party, presenceById, toast } from './store';
import { bannedMessage, phase } from './session';

let wired = false;

/** Registers the social socket handlers once. */
export function wireSocial(): void {
  if (wired) return;
  wired = true;

  social.on('hello', (m) => {
    onlineCount.value = m.online;
  });

  social.on('chat', (m) => appendChat(m.msg));
  social.on('chat_history', (m) => setChatHistory(m.channel, m.msgs));

  social.on('presence', (m) => {
    presenceById.value = { ...presenceById.value, [m.userId]: { presence: m.presence, activity: m.activity } };
  });

  social.on('friend_request', (m) => {
    if (mutedUsers.value.has(m.from.id)) return;
    audio.play('pickup');
    toast(`${m.from.name} wants to be friends`, {
      kind: 'social',
      body: `Level ${m.from.level}${m.from.clanTag ? ` · [${m.from.clanTag}]` : ''}`,
      actions: [
        { label: 'Accept', primary: true, onClick: () => acceptFriend(m.from.id, m.from.name) },
        { label: 'Decline', onClick: () => declineFriend(m.from.id) },
      ],
    });
    void loadFriends().catch(() => {});
  });

  social.on('friend_update', () => void loadFriends().catch(() => {}));

  social.on('party', (m) => {
    const before = party.value;
    party.value = m.party;
    const myId = me.value?.id;
    if (before && before.members.length > 1 && !m.party) toast("You're no longer in a party", { kind: 'info' });
    else if (m.party && before && myId && !m.party.members.some((x) => x.id === myId)) party.value = null;
  });

  social.on('party_invite', (m) => {
    if (mutedUsers.value.has(m.from.id)) return;
    audio.play('pickup');
    toast(`${m.from.name} invited you to their party`, {
      kind: 'social',
      actions: [
        { label: 'Join', primary: true, onClick: () => joinParty(m.partyId) },
        { label: 'Not now', onClick: () => {} },
      ],
      ttl: 20000,
    });
  });

  social.on('match_found', (m) => onMatchFound(m.match));
  social.on('queue', (m) => onQueueUpdate(m.state, m.waited));
  social.on('rewards', (m) => onRewards(m.rewards));
  social.on('wallet', (m) => setWallet(m.wallet));

  social.on('inbox', (m) => {
    const before = me.value?.unreadInbox ?? 0;
    if (me.value) me.value = { ...me.value, unreadInbox: m.unread };
    if (m.unread > before) {
      toast('New message in your inbox', { kind: 'info' });
      void loadInbox().catch(() => {});
    }
  });

  social.on('clan_update', () => {
    void refreshMe().then(() => loadMyClan().catch(() => {}));
  });

  social.on('notice', (m) => {
    toast(m.text, { kind: m.kind === 'error' ? 'error' : m.kind === 'warning' ? 'warning' : 'info', ttl: 7000 });
  });

  social.on('kicked', (m) => {
    if (/ban/i.test(m.reason)) {
      bannedMessage.value = m.reason;
      phase.value = 'banned';
      return;
    }
    toast('Disconnected from social services', { kind: 'warning', body: m.reason, ttl: 10000 });
  });

  // Presence follows tab visibility in the menus; friend data refreshes when the tab comes back.
  document.addEventListener('visibilitychange', () => {
    const inMatch = !!launch.value;
    if (document.visibilityState === 'visible') {
      social.kick();
      if (!inMatch) social.setPresence('lobby');
      if (friends.value) void loadFriends().catch(() => {});
    } else if (!inMatch) {
      social.setPresence('away');
    }
  });
  addEventListener('online', () => social.kick());
}
